import React from "react";
import { Accordion } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import debounce from "lodash.debounce";
import { AccordionSkeleton } from "../../components";
import { ApiHelper, ArrayHelper, MessageInterface, ToastHelper } from "../../helpers";


interface Props {
  refreshKey: string;
}

export const MessagesSidebar: React.FC<Props> = (props) => {
  const location = useLocation();
  const [messages, setMessages] = React.useState<MessageInterface[]>(null);
  const [selectedMessage, setSelectedMessage] = React.useState<MessageInterface>(null);
  const [filteredMessages, setFilteredMessages] = React.useState<MessageInterface[]>(null);
  const [searchActive, setSearchActive] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [showUnusedMessages, setShowUnusedMessages] = React.useState(false);
  const [selectMode, setSelectMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);

  const init = () => {
    ApiHelper.get<MessageInterface[]>("/messages")
      .then((data) => {
        setMessages(data || []);
        setFilteredMessages(data || []);
      })
      .catch(err => {
        console.error("Failed to load messages:", err);
        ToastHelper.error("Failed to load messages");
      });
  };

  const updateSelected = () => {
    let result: MessageInterface = null;
    const parts = window.location.href.split("?")[0].split("/");
    const id = parseInt(parts[parts.length - 1]);
    messages?.forEach((m) => {
      if (m.id === id) result = m;
    });
    setSelectedMessage(result);
  };

  React.useEffect(init, [props.refreshKey]);
  React.useEffect(updateSelected, [messages, location.pathname]);

  const applyFilters = (term: string) => {
    let filtered = messages;
    if (term) {
      filtered = filtered?.filter((m) => m.name?.toLowerCase().includes(term));
    }
    setFilteredMessages(filtered);
  };

  // Debounced filter application - prevents filtering on every keystroke
  const debouncedApplyFilters = React.useMemo(
    () => debounce(applyFilters, 300),
    [messages] // eslint-disable-line
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    debouncedApplyFilters(term);
  };

  const handleToggleMessages = () => {
    setShowUnusedMessages((prevState) => {
      const newState = !prevState;
      if (newState) {
        const unusedMessages = messages?.filter((m) => m.isExistInPlaylist === 0);
        setFilteredMessages(unusedMessages);
      } else {
        setFilteredMessages(messages);
      }
      return newState;
    });
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedIds([]);
  };

  const toggleMessageSelection = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    const allIds = filteredMessages?.map(m => m.id) || [];
    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm("Are you sure you wish to delete " + selectedIds.length + " message(s)?")) return;
    try {
      await Promise.all(selectedIds.map(id => ApiHelper.delete("/messages/" + id)));
      ToastHelper.success("Deleted " + selectedIds.length + " message(s)");
      setSelectedIds([]);
      setSelectMode(false);
      init();
    } catch (err) {
      console.error("Failed to delete messages:", err);
      ToastHelper.error("Failed to delete some messages");
    }
  };

  const getMessages = () => {
    if (filteredMessages === null) return <AccordionSkeleton sections={4} />;
    else return getMessageAccordion();
  };

  const getMessageAccordion = () => {
    if (filteredMessages) {
      const sections: React.ReactElement[] = [];
      filteredMessages?.forEach((m) => {
        if (!m.category) m.category = "Uncategorized";
      });
      const categories = ArrayHelper.getStrings(filteredMessages, "category");
      categories.forEach((c) => {
        const catMessages = ArrayHelper.getAll(filteredMessages, "category", c);
        const catContent = getMessagesRows(catMessages);
        sections.push(getSection(c, c, catContent));
      });
      const defaultActiveKey = categories.length > 5 ? "" : categories[0];
      return (
          <Accordion id="messagesSidebar" defaultActiveKey={defaultActiveKey}>
            {sections}
          </Accordion>
      );
    }
  };

  const getSection = (eventKey: string, title: string, contents: React.ReactElement) => {
    return (
        <Accordion.Item key={eventKey} eventKey={eventKey}>
          <Accordion.Header>{title}</Accordion.Header>
          <Accordion.Body>{contents}</Accordion.Body>
        </Accordion.Item>
    );
  };

  const getMessagesRows = (messages: MessageInterface[]) => {
    const result: React.ReactElement[] = [];
    messages.forEach((m) => {
      let url = "/messages/" + m.id;
      if (m.shape !== "16:9") url += "?shape=" + m.shape;
      const className = m.id === selectedMessage?.id ? "active" : "";
      if (selectMode) {
        const isChecked = selectedIds.includes(m.id);
        result.push(
          <li key={"m" + m.id} className="nav-item">
            <span className={className} style={{cursor: "pointer"}} onClick={() => toggleMessageSelection(m.id)}>
              <input type="checkbox" checked={isChecked} onChange={() => toggleMessageSelection(m.id)} style={{marginRight: 8}} onClick={(e) => e.stopPropagation()} />
              {m.name || "(no name)"}
            </span>
          </li>
        );
      } else {
        result.push(
          <li key={"m" + m.id} className="nav-item">
            <Link className={className} to={url}>
              {m.name || "(no name)"}
            </Link>
          </li>
        );
      }
    });
    return <ul className="nav flex-column">{result}</ul>;
  };

  return (
      <div className="sidebar-container">
        <div className="sidebar-header">
          {searchActive ? (
              <div className="sidebar-search-active">
                <input
                    data-testid="messages-search-input"
                    type="text"
                    className="form-control sidebar-search-input"
                    placeholder="Search messages..."
                    value={searchTerm}
                    onChange={handleSearch}
                    onBlur={() => {
                      if (!searchTerm) setSearchActive(false);
                    }}
                    autoFocus
                />
                <button
                    data-testid="messages-sidebar-action-button"
                    className="sidebar-filter-btn"
                    onClick={handleToggleMessages}
                >
                  {showUnusedMessages ? "Show All" : "Show Unused"}
                </button>
              </div>
          ) : (
              <>
                <h3 className="sidebar-title">Messages</h3>
                <div className="sidebar-actions">
                  <button
                      className="sidebar-icon-btn"
                      onClick={() => setSearchActive(true)}
                      title="Search messages"
                  >
                    <i className="fas fa-search"></i>
                  </button>
                  <button
                      className={selectMode ? "sidebar-icon-btn active" : "sidebar-icon-btn"}
                      onClick={toggleSelectMode}
                      title={selectMode ? "Exit select mode" : "Select messages"}
                  >
                    <i className="fas fa-check-square"></i>
                  </button>
                  <Link to="/messages" className="sidebar-add-btn" title="Create new message">
                    <i className="fas fa-plus"></i>
                  </Link>
                </div>
              </>
          )}
        </div>
        {selectMode && (
          <div className="sidebar-bulk-actions" style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderBottom: "1px solid #dee2e6"}}>
            <button className="btn btn-sm btn-outline-secondary" onClick={handleSelectAll}>
              {selectedIds.length === (filteredMessages?.length || 0) ? "Deselect All" : "Select All"}
            </button>
            <button className="btn btn-sm btn-danger" onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
              <i className="fas fa-trash"></i> Delete ({selectedIds.length})
            </button>
          </div>
        )}
        <div className="sidebar-content">
          {getMessages()}
        </div>
      </div>
  );
};
